import { TaskPriority } from '@business/constants/task';
import { UserId, AccountResponse } from '@business/entities/account';
import { CommentResponse } from '@business/entities/comment';
import { CompactContactResponse, ContactId } from '@business/entities/contact';
import { OrgId, PaymentId, PaymentSuccessResponse } from '@business/entities/organization';
import {
  CompactProjectResponse,
  ProjectId,
  ProjectRequest,
  ProjectResponse,
  ProjectUpdateRequest,
  ProjectPermissionRequest,
  ShareProjectResponse,
  ProjectUserResponse,
  ProjectLinkResponse,
  VeryCompactProjectResponse,
} from '@business/entities/project';
import { ProjectSectionResponse, ProjectStepResponse, CompactProjectSectionResponse } from '@business/entities/project/ProjectSectionResponse';
import { ProjectTemplateId } from '@business/entities/project/TemplateResponse';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { Endpoint } from '../endpoint';
import { ProposalTemplateId } from '@business/entities/proposal';
import { InviteUsersFormData } from '@modules/projects/ProjectOverlay/InviteUserModal/InviteUserModal';

export function fetchProjects(options: { search?: string } = {}) {
  const params = R.objectCompact({ search: options.search });
  return Endpoint.get<CompactProjectResponse[]>(`@gc/organizations/project`, { params });
}

export function searchProjects(options: { search?: string } = {}) {
  const params = R.objectCompact({ search: options.search });
  return Endpoint.get<VeryCompactProjectResponse[]>(`@gc/organizations/project/search`, { params });
}

export function getProjectSections(id: ProjectId) {
  return Endpoint.get<CompactProjectSectionResponse[]>(`@gc/organizations/project/project-sections/${id}`);
}

export function fetchProject(id: ProjectId) {
  return Endpoint.get<ProjectResponse>(`@gc/organizations/project/project/${id}`);
}

export function createProject(orgId: OrgId, data: ProjectRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/${orgId}`, { formData, hasFiles: true });
}
export function editProject(id: ProjectId, data: ProjectRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/edit/${id}`, { formData, hasFiles: true });
}

export function updateProject(id: ProjectId, data: ProjectUpdateRequest) {
  return Endpoint.patch<ProjectResponse>(`@gc/organizations/project/${id}`, { data });
}

export function updateProjectUsers(id: ProjectId, users: ProjectUserResponse[]) {
  return Endpoint.post<ProjectUserResponse[]>(`@gc/organizations/project/project-users/${id}`, { data: { users } });
}

export function createProjectUser(id: ProjectId, data: InviteUsersFormData) {
  return Endpoint.post<ProjectUserResponse[]>(`@gc/organizations/project/create-project-user/${id}`, { data });
}

export function updateProjectAction(id: ProjectId, actionId: string, actionName: string) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/update-action/${id}`, { data: { actionId, actionName } });
}

export function updateDueDate(id: ProjectId, dueDate: number) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/due-date/${id}`, { data: { dueDate } });
}

export function updateStartDate(id: ProjectId, startDate: number) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/start-date/${id}`, { data: { startDate } });
}

export function updateBoard(id: ProjectId, data: Pick<ProjectUpdateRequest, 'board' | 'stage'>) {
  return Endpoint.patch<CompactProjectResponse>(`@gc/organizations/project/board/${id}`, { data });
}

export function deleteProject(projectId: ProjectId) {
  return Endpoint.delete<void>(`@gc/organizations/project/${projectId}`);
}
export function archiveProject(projectId: ProjectId) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/archive/${projectId}`);
}

export function sendInvoice(projectId: ProjectId, emails?: string[], amount?: number, body?: string) {
  return Endpoint.post<void>(`@gc/organizations/project/send-invoice/${projectId}`, { data: { emails, amount, body } });
}

export function processPayment(projectId: ProjectId, paymentId: PaymentId, amount: number) {
  return Endpoint.post<PaymentSuccessResponse>(`@gc/organizations/project/process-payment/${projectId}`, { data: { amount, paymentId } });
}

export function manualPayment(projectId: ProjectId, paymentId: PaymentId, amount: number, paymentDate: number) {
  return Endpoint.post<void>(`@gc/organizations/project/manual-payment/${projectId}`, { data: { amount, paymentId, paymentDate } });
}

export function setPriority(projectId: ProjectId, priority: string) {
  return Endpoint.patch<CompactProjectResponse>(`@gc/organizations/project/priority/${projectId}`, { data: { priority } });
}

export function setStage(projectId: ProjectId, stageId: number) {
  return Endpoint.patch<CompactProjectResponse>(`@gc/organizations/project/stage/${projectId}`, { data: { stageId } });
}

export function selectManager(projectId: ProjectId, manager: UserId) {
  return Endpoint.patch<CompactProjectResponse>(`@gc/organizations/project/select-manager/${projectId}`, { data: { manager } });
}

export function markComplete(projectId: ProjectId, completedAt: number) {
  return Endpoint.patch<void>(`@gc/organizations/project/complete/${projectId}`, { data: { completedAt } });
}

export function selectTemplate(projectId: ProjectId, template: ProjectTemplateId) {
  return Endpoint.patch<CompactProjectResponse>(`@gc/organizations/project/select-template/${projectId}`, { data: { template } });
}

export function changeTemplate(projectId: ProjectId, template: ProjectTemplateId) {
  return Endpoint.patch<CompactProjectResponse>(`@gc/organizations/project/change-template/${projectId}`, { data: { template } });
}

export function toggleStep(stepId: number, completeAll?: boolean, tenantId?: string) {
  return Endpoint.post<ProjectSectionResponse>(`@gc/organizations/project/toggle-step/${stepId}`, { data: { completeAll, tenantId } });
}

export function loadStep(stepId: number) {
  return Endpoint.get<ProjectSectionResponse>(`@gc/organizations/project/load-step/${stepId}`);
}
/** Section **/
export function createSection(id: ProjectId, name: string, tenantId?: string, isPublic = false) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/section/${id}`, { data: { name, tenantId, isPublic } });
}

export function updateSection(id: string, name: string, tenantId?: string, isPublic = false, projectId?: ProjectId) {
  return Endpoint.patch<ProjectSectionResponse[]>(`@gc/project/sections/section/${id}`, { data: { name, tenantId, isPublic, projectId } });
}

export function deleteSection(id: string, tenantId?: string) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/delete-section/${id}`, { data: { tenantId } });
}

export function duplicateSection(id: string, tenantId?: string) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/duplicate-section/${id}`, { data: { tenantId } });
}

export function sortSections(ids: string[], tenantId?: string) {
  return Endpoint.post<void>(`@gc/project/sections/sort-section`, { data: { ids, tenantId } });
}

export function updateSectionClose(sectionId: string, isClosed: boolean) {
  return Endpoint.post<void>(`@gc/project/sections/section-close/${sectionId}`, { data: { isClosed } });
}

/** Step **/
export function fetchStep(id: string, tenantId?: string) {
  return Endpoint.get<ProjectStepResponse>(`@gc/project/sections/step/${id}`, { params: { tenantId } });
}
export function createStep(id: string, data: Partial<ProjectStepResponse>, parentStepId?: string, tenantId?: string, projectId?: ProjectId) {
  data.parentId = parentStepId;
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/step/${id}`, { data: { ...data, tenantId, projectId } });
}

export function duplicateStep(id: string, tenantId?: string) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/duplicate-step/${id}`, { data: { tenantId } });
}

export function updateStep(id: string, data: Partial<ProjectStepResponse>, tenantId?: string) {
  return Endpoint.patch<ProjectSectionResponse>(`@gc/project/sections/step/${id}`, { data: { ...data, tenantId } });
}

export function deleteStep(id: string, tenantId?: string) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/delete-step/${id}`, { data: { tenantId } });
}

export function sortSectionSteps(ids: string[], tenantId?: string) {
  return Endpoint.post<void>(`@gc/project/sections/sort-step`, { data: { ids, tenantId } });
}

export function assignStep(stepId: string, data: Partial<ProjectStepResponse>) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/step-assign/${stepId}`, { data });
}

export function assignSection(projectId: ProjectId, sectionId: string, users: UserId[]) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/section-assign/${sectionId}`, { data: { users, projectId } });
}

export function assignSectionUsers(projectId: ProjectId, sectionId: string, users: UserId[]) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/section-assign-users/${sectionId}`, { data: { users, projectId } });
}

export function updateNotes(projectId: ProjectId, notes: string) {
  return Endpoint.patch<void>(`@gc/organizations/project/update-notes/${projectId}`, { data: { notes } });
}

export function generateBrief(projectId: ProjectId) {
  return Endpoint.get<ProjectResponse>(`@gc/organizations/project/brief/${projectId}`, {});
}

export function updateDescription(projectId: ProjectId, description: string) {
  return Endpoint.patch<void>(`@gc/organizations/project/update-description/${projectId}`, { data: { description } });
}

export function updateTitle(projectId: ProjectId, title: string) {
  return Endpoint.patch<ProjectResponse>(`@gc/organizations/project/update-title/${projectId}`, { data: { title } });
}

export function createStepComment(id: string, data: Partial<CommentResponse>) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/step-comment/${id}`, { data });
}

export function updateContactTimeZone(projectId: ProjectId, contactId: ContactId, timeZone: string) {
  return Endpoint.post<CompactContactResponse>(`@gc/organizations/project/update-contact/${projectId}`, { data: { contactId, timeZone } });
}

export function shareProject(data: ProjectPermissionRequest, id: string) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/share/${id}`, { data });
}

export function projectShared(appId: string, projectId: string) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/accept/${projectId}`, { data: { appId } });
}

export function shareAuthProject(data: ShareProjectResponse) {
  return Endpoint.post<AccountResponse[]>(`@gc/organizations/project/share-auth-project/${data.projectId}`, { data });
}

export function sortProjectSectionSteps(step: ProjectStepResponse, toColumn: any, toPosition: number) {
  return Endpoint.post<ProjectStepResponse[]>(`@gc/project/sections/sort-project-step`, { data: { step, toColumn, toPosition } });
}

export function sortProjectSections(section: ProjectSectionResponse, fromPosition: any, toPosition: number, projectId: ProjectId) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/sort-project-section`, {
    data: { section, fromPosition, toPosition, projectId },
  });
}

export function assignProjectStep(stepId: string, data: string[]) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/assign-project-step/${stepId}`, { data: { data } });
}

export function setProjectStepPriority(stepId: string, priority: TaskPriority) {
  return Endpoint.post<ProjectSectionResponse[]>(`@gc/project/sections/set-project-step-priority/${stepId}`, { data: { priority } });
}

export function orderTodos(projectId: ProjectId, items: any) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/order-todos/${projectId}`, { data: { items } });
}

export function addProposal(projectId: ProjectId, ProposalTemplateId: ProposalTemplateId) {
  return Endpoint.post<ProjectResponse>(`@gc/organizations/project/add-proposal/${projectId}`, { data: { ProposalTemplateId } });
}

export function processLink(appId: string, projectId: string) {
  return Endpoint.get<ProjectLinkResponse>(`@gc/organizations/project/process-link`, { params: { appId, projectId } });
}
